/**
 * Tenant Selector Component
 *
 * Dropdown that allows system admins to select which tenant's data to view.
 * Only visible to admin users. Persists selection via Redux + localStorage.
 */

import { useCallback, useState, useMemo } from 'react'
import { useTranslation } from '@spcc/core'
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  Badge,
  cn,
  Building2,
  Check,
  ChevronsUpDown,
} from '@spcc/uikit'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import {
  selectIsAdmin,
  selectSelectedTenant,
  selectTenantsList,
  selectTenantDisplayName,
  adminContextActions,
  type TenantSummary,
} from '@spcc/domains/admin-context'

// ============================================
// Constants
// ============================================

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  suspended: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  inactive: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
}

// ============================================
// Helper Functions
// ============================================

function filterTenantsBySearch(tenants: TenantSummary[], search: string): TenantSummary[] {
  if (!search.trim()) return tenants
  const term = search.toLowerCase()
  return tenants.filter(
    (t) => t.name.toLowerCase().includes(term) || t.code.toLowerCase().includes(term)
  )
}

function getStatusBadgeClass(status: string): string {
  return STATUS_COLORS[status] ?? STATUS_COLORS.inactive
}

// ============================================
// Component
// ============================================

export function TenantSelector() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  const isAdmin = useAppSelector(selectIsAdmin)
  const selectedTenant = useAppSelector(selectSelectedTenant)
  const tenantsList = useAppSelector(selectTenantsList)
  const displayName = useAppSelector(selectTenantDisplayName)

  // Filter tenants by search - must be called before any conditional return
  const filteredTenants = useMemo(
    () => filterTenantsBySearch(tenantsList, search),
    [tenantsList, search]
  )

  const handleSelectTenant = useCallback(
    (tenant: TenantSummary) => {
      // Skip if already selected
      if (selectedTenant?.id === tenant.id) {
        setOpen(false)
        return
      }
      dispatch(adminContextActions.selectTenant(tenant))
      // Reload page to refresh all data with new tenant filter
      window.location.reload()
    },
    [dispatch, selectedTenant]
  )

  const handleClearSelection = useCallback(() => {
    // Skip if already cleared
    if (!selectedTenant) {
      setOpen(false)
      return
    }
    dispatch(adminContextActions.clearTenantSelection())
    // Reload page to refresh all data without tenant filter
    window.location.reload()
  }, [dispatch, selectedTenant])

  // Don't render if not admin - AFTER all hooks
  if (!isAdmin) return null

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-[200px] justify-between"
        >
          <div className="flex items-center gap-2 truncate">
            <Building2 className="h-4 w-4 shrink-0 opacity-70" />
            <span className="truncate">{displayName}</span>
          </div>
          <ChevronsUpDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="end">
        <Command>
          <CommandInput
            placeholder={t('admin.searchTenants', 'Search tenants...')}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>{t('admin.noTenantsFound', 'No tenants found.')}</CommandEmpty>
            <CommandGroup>
              {/* All Tenants option */}
              <CommandItem
                value="all-tenants"
                onSelect={handleClearSelection}
                className="flex items-center justify-between"
              >
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 opacity-70" />
                  <span>{t('admin.allTenants', 'All Tenants')}</span>
                </div>
                {!selectedTenant && <Check className="h-4 w-4" />}
              </CommandItem>

              {/* Divider */}
              <div className="my-1 h-px bg-border" />

              {/* Tenant list */}
              {filteredTenants.map((tenant) => (
                <CommandItem
                  key={tenant.id}
                  value={tenant.name}
                  onSelect={() => handleSelectTenant(tenant)}
                  className="flex items-center justify-between"
                >
                  <div className="flex flex-col gap-0.5">
                    <span className="font-medium">{tenant.name}</span>
                    <span className="text-xs text-muted-foreground">{tenant.code}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className={cn('text-xs', getStatusBadgeClass(tenant.status))}>
                      {tenant.status}
                    </Badge>
                    {selectedTenant?.id === tenant.id && <Check className="h-4 w-4" />}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
